import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import type { PropsWithChildren } from 'react';
import type { AnyAction, EnhancedStore } from '@reduxjs/toolkit';
import type { Persistor } from 'redux-persist/lib/types';
import type { Middleware } from 'redux';
import type { SagaMiddleware } from 'redux-saga';
import { Loader } from './components';
import { CoreMetricsProvider, CoreProviders, LoggerProvider } from './providers';
import { useApp } from './hooks';
import { LoggerService } from './services';
import { terminateAPP } from './store/modules/common/actions';

type InitStore = () => {
  store: EnhancedStore<unknown, AnyAction, (Middleware | SagaMiddleware<object>)[]>; // TODO: add context type
  persistor: Persistor;
  saga: SagaMiddleware<object>;
};

function CoreApp({ appName, children }: PropsWithChildren<{ appName: string }>) {
  useApp(appName);

  return <CoreProviders>{children}</CoreProviders>;
}

// TODO: probably has to be in HOC folder
export const initApp = <T extends InitStore>(appName: string, initStore: T) => {
  const { store, persistor, saga } = initStore();

  const loggerService = new LoggerService(appName);

  saga.setContext({ LoggerService: loggerService });

  // FIX: cheap solution to unsubscribe core events when app is going to die.
  // To make it proper, have to create Shell(Global)Manager to keep stores, sagas, contexts.
  const terminate = () => {
    store.dispatch(terminateAPP());
  };

  const App = function ({ children }: PropsWithChildren<unknown>) {
    return (
      <LoggerProvider value={loggerService}>
        <CoreMetricsProvider appName={appName}>
          <Provider store={store}>
            <PersistGate loading={<Loader block size={100} />} persistor={persistor}>
              <CoreApp appName={appName}>{children}</CoreApp>
            </PersistGate>
          </Provider>
        </CoreMetricsProvider>
      </LoggerProvider>
    );
  };

  return { App, terminate };
};
